import { formatDateDisplay } from '@/util/formatter/formatDate';

/**
 * Persistence/audit column names (camelCased leaf keys, compared case-insensitively) that live on
 * every entity via `AuditableBaseFull` but are NOT supplier-editable form fields — row identity,
 * created/modified/deleted bookkeeping, foreign keys, concurrency tokens. They must never surface
 * in the ERP Sync diff cards or the "Last sent to ERP" snapshot: an approver reviews business
 * fields (certificate number, expiry date, …), not a row's identity/audit noise, and they must not
 * inflate the change count (Epic #4184 review-feedback fix — a COIDA update is a delete+reinsert,
 * so `id`/`dateCreated`/`dateModified` all "change" on every edit).
 *
 * Keep this in step with the backend source of truth, `DiffFieldRegistry.IsIgnored`
 * (B1NG-Service-Integration-Gateway).
 */
export const IGNORED_SNAPSHOT_KEYS: ReadonlySet<string> = new Set([
  'id',
  'companyid',
  'suppliercompanyid',
  'clientcompanyid',
  'datecreated',
  'datemodified',
  'datedeleted',
  'isdeleted',
  'createdby',
  'modifiedby',
  'deletedby',
  'createddate',
  'modifieddate',
  'updateddate',
  'createdat',
  'updatedat',
  'rowversion',
  'concurrencystamp',
  'etag'
]);

/** True when `key` (any casing) is a system/audit field that must be hidden from the review. */
export const isIgnoredSnapshotKey = (key: string): boolean =>
  IGNORED_SNAPSHOT_KEYS.has(key.trim().toLowerCase());

/**
 * Trailing property-name segment of a dotted/indexed diff path, e.g.
 * `accounts[0].accountNumber` -> `accountNumber`, `certificateNumber` -> `certificateNumber`.
 * Mirrors the backend `DiffSummaryBuilder.GetLeafSegment`.
 */
export const leafKeyFromPath = (path: string): string => {
  const afterLastDot = path.slice(path.lastIndexOf('.') + 1);
  const bracket = afterLastDot.indexOf('[');
  return bracket >= 0 ? afterLastDot.slice(0, bracket) : afterLastDot;
};

/**
 * Everything BEFORE the final `.<leaf>` segment of a dotted diff path, e.g.
 * `"Tax Clearance Certificate.documentName"` -> `"Tax Clearance Certificate"`. Returns an empty
 * string when the path has no dot (a bare leaf with no parent segment to group by).
 *
 * Used for the Documents category's whole-set-snapshot shape, where the document TYPE NAME (which
 * may itself contain spaces) is the path prefix and multiple document types can appear in one diff
 * — the mirror image of {@link leafKeyFromPath}, which discards this same prefix.
 */
export const typePrefixFromPath = (path: string): string => {
  const lastDot = path.lastIndexOf('.');
  return lastDot >= 0 ? path.slice(0, lastDot) : '';
};

// Matches an ISO-8601 date or date-time, e.g. `2026-07-13` or
// `2026-07-13T18:40:15.8002633+00:00`. Deliberately anchored/strict so plain numeric business
// values (certificate numbers, account numbers, registration numbers) are never mistaken for a
// date and reformatted.
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Formats a raw snapshot/diff leaf value for display: an ISO date/date-time string becomes a human
 * date (e.g. `13 Jul 2026`); everything else passes through unchanged. Supplier business dates
 * (issue / expiry / registration) are date-only by nature, so the time-of-day is intentionally
 * dropped — matching the `formatDateDisplay` used elsewhere on the ERP Sync tab.
 */
export const formatSnapshotValue = (value: string): string => {
  const trimmed = value.trim();
  if (ISO_DATE_RE.test(trimmed)) {
    const formatted = formatDateDisplay(trimmed);
    if (formatted) {
      return formatted;
    }
  }
  return value;
};

export interface CategorySnapshotField {
  key: string;
  label: string;
}

export interface SnapshotDisplayRow {
  key: string;
  label: string;
  value: string;
}

/**
 * Per-category display schema for the "Last sent to ERP" snapshot: the business fields to surface,
 * in order, with human labels. Snapshots are the raw persisted entities, which carry FK lookup ids
 * (`bankId`, `accountTypeId`, `countryId`, `contactTypeId`), nested navigation objects, and
 * technical flags that are meaningless to an approver — an allowlist both cleans and orders the
 * output. Matched case-insensitively by SupplierUpdateCategory name; categories with no schema fall
 * back to the generic flatten. NOTE: the publisher now resolves lookup FKs to readable names before
 * publishing — SA `bankId`/`accountTypeId` and foreign free-text both arrive as a single `bankName`/
 * `accountType`, so the schema keys below are the resolved names (not the raw FK ids).
 */
export const CATEGORY_SNAPSHOT_SCHEMA: Record<string, CategorySnapshotField[]> = {
  'Bank Details': [
    { key: 'accountName', label: 'Account holder' },
    // Bank name arrives in one of three forms depending on account type / backend version:
    // `bankId` (SA lookup id — resolved to a name by ID_LOOKUP_FIELDS), `textBankName` (foreign
    // free-text), or `bankName` (already-resolved). All are surfaced and labelled "Bank".
    { key: 'bankName', label: 'Bank' },
    { key: 'textBankName', label: 'Bank' },
    { key: 'bankId', label: 'Bank' },
    { key: 'accountNumber', label: 'Account number' },
    { key: 'branchName', label: 'Branch name' },
    { key: 'branchCode', label: 'Branch code' },
    { key: 'swiftCode', label: 'SWIFT code' },
    // Account type: `accountTypeId` (SA lookup id — resolved), `textAccountType` (foreign), or
    // `accountType` (already-resolved).
    { key: 'accountType', label: 'Account type' },
    { key: 'textAccountType', label: 'Account type' },
    { key: 'accountTypeId', label: 'Account type' },
    // Bank account country — a countryId FK, resolved to the country name by ID_LOOKUP_FIELDS.
    { key: 'countryId', label: 'Country' },
    { key: 'dateOfIssue', label: 'Date of issue' },
    { key: 'isPrimary', label: 'Primary account' }
  ],
  'Contact Details': [
    { key: 'name', label: 'Name' },
    { key: 'designation', label: 'Designation' },
    { key: 'email', label: 'Email address' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'landLine', label: 'Landline' },
    { key: 'fax', label: 'Fax' },
    { key: 'website', label: 'Website' },
    { key: 'idNumber', label: 'ID number' },
    { key: 'username', label: 'Username' }
  ],
  COIDA: [
    { key: 'cfRegistrationNumber', label: 'CF registration number' },
    { key: 'certificateNumber', label: 'Certificate number' },
    { key: 'dateOfIssue', label: 'Date of issue' },
    { key: 'expiryDate', label: 'Expiry date' }
  ],
  // The Documents snapshot is now a whole-set snapshot keyed by document type name (the diff path
  // prefix, e.g. "Tax Clearance Certificate.documentName") rather than a single document — see
  // documentDiff.ts for the per-type grouping/row-building. `documentType` and `uploadedAt` are no
  // longer published per document, and `blobReference` is deliberately NOT allowlisted here so the
  // document GUID stays hidden from rendered rows (it is only used internally to fetch the file).
  Documents: [{ key: 'documentName', label: 'File name' }]
};

/** Case-insensitive lookup of a record's value for a schema key. */
const findRecordValue = (record: Record<string, unknown>, key: string): unknown => {
  const lower = key.toLowerCase();
  const match = Object.keys(record).find(
    (recordKey) => recordKey.toLowerCase() === lower
  );
  return match === undefined ? undefined : record[match];
};

/** Formats a raw record value for display, or null if it should be omitted (empty / nested). */
const formatRecordValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    return null; // nested nav object / array — not surfaced through the flat schema
  }
  return formatSnapshotValue(String(value));
};

/** Returns the display schema for a category name, or undefined when none is defined. */
export const getCategorySnapshotSchema = (
  categoryName?: string
): CategorySnapshotField[] | undefined => {
  if (!categoryName) {
    return undefined;
  }
  const lower = categoryName.toLowerCase();
  const key = Object.keys(CATEGORY_SNAPSHOT_SCHEMA).find(
    (schemaKey) => schemaKey.toLowerCase() === lower
  );
  return key ? CATEGORY_SNAPSHOT_SCHEMA[key] : undefined;
};

/**
 * Builds ordered, labeled display rows for one record using a category schema — only the schema's
 * business fields, only those with a value. `prefix` (e.g. "1") disambiguates multiple records
 * (a supplier with several bank accounts / contacts).
 */
export const schemaSnapshotRows = (
  schema: CategorySnapshotField[],
  record: Record<string, unknown>,
  prefix = ''
): SnapshotDisplayRow[] =>
  schema
    .map((field) => ({
      field,
      value: formatRecordValue(findRecordValue(record, field.key))
    }))
    .filter(
      (entry): entry is { field: CategorySnapshotField; value: string } =>
        entry.value !== null
    )
    .map(({ field, value }) => ({
      key: prefix ? `${prefix}.${field.key}` : field.key,
      label: prefix ? `${prefix} › ${field.label}` : field.label,
      value
    }));

/**
 * Decides how a single diffed leaf is presented on the Pending tab for a given category. Schema'd
 * categories (Bank Details, Contact Details, COIDA, Documents) allowlist their business fields and
 * relabel them to match the supplier's edit form — so the diff shows only what the supplier actually
 * changed, not raw entity noise (FK ids, auto-populated lookup columns, technical flags) that made
 * "wrong fields" appear. Un-schema'd categories fall back to hiding audit/identity fields and
 * keeping the backend-supplied label.
 */
/** The lookup lists the reviewer UI can resolve a raw FK id against, at display time only. */
export type IdLookupName =
  | 'companyTypes'
  | 'countries'
  | 'financialYearEndMonths'
  | 'provinces'
  | 'banks'
  | 'bankAccountTypes';

/**
 * Diff leaves that arrive as a raw numeric FK id but should DISPLAY as the resolved lookup name
 * (e.g. `companyTypeId` = 3 shown as "Pty Ltd"). This is a pure display concern — the snapshot and
 * the persisted record are unchanged; the reviewer UI just swaps the id for its name using lookup
 * lists it already fetches. Keyed by the camelCase diff leaf property name (matched case-insensitively).
 */
export const ID_LOOKUP_FIELDS: Record<string, { label: string; lookup: IdLookupName }> = {
  companyTypeId: { label: 'Company type', lookup: 'companyTypes' },
  countryId: { label: 'Country', lookup: 'countries' },
  financialYearEndMonthId: {
    label: 'Financial year end',
    lookup: 'financialYearEndMonths'
  },
  // SA bank accounts store the bank / account type as a numeric lookup id; resolve to the name.
  bankId: { label: 'Bank', lookup: 'banks' },
  accountTypeId: { label: 'Account type', lookup: 'bankAccountTypes' }
};

/**
 * Diff leaves that arrive as a JSON-stringified array of FK ids (e.g. geographicalLocations =
 * `["1","5"]`, a multi-select of province ids) and should DISPLAY as a comma-joined list of names.
 * Display-only, same as {@link ID_LOOKUP_FIELDS}.
 */
export const ID_LIST_LOOKUP_FIELDS: Record<
  string,
  { label: string; lookup: IdLookupName }
> = {
  geographicalLocations: { label: 'Geographical locations', lookup: 'provinces' }
};

/** Case-insensitive lookup of the id-resolution config for a diff leaf key. */
export const getIdLookupField = (
  leafKey: string
): { label: string; lookup: IdLookupName } | undefined => {
  const lower = leafKey.toLowerCase();
  const key = Object.keys(ID_LOOKUP_FIELDS).find((k) => k.toLowerCase() === lower);
  return key ? ID_LOOKUP_FIELDS[key] : undefined;
};

/** Case-insensitive lookup of the id-LIST-resolution config for a diff leaf key. */
export const getIdListLookupField = (
  leafKey: string
): { label: string; lookup: IdLookupName } | undefined => {
  const lower = leafKey.toLowerCase();
  const key = Object.keys(ID_LIST_LOOKUP_FIELDS).find((k) => k.toLowerCase() === lower);
  return key ? ID_LIST_LOOKUP_FIELDS[key] : undefined;
};

/**
 * Maps a raw id value (as it appears in a diff, e.g. "3") to its lookup name. Returns the raw value
 * unchanged when it is empty, the map is unavailable (still loading), or the id has no match — so the
 * reviewer never sees a blank where an id was.
 */
export const resolveLookupValue = (
  rawValue: string | null,
  namesById: Record<string, string> | undefined
): string | null => {
  if (rawValue === null || rawValue === '' || !namesById) {
    return rawValue;
  }
  return namesById[rawValue] ?? rawValue;
};

/**
 * Resolves a JSON-stringified array of ids (e.g. `["1","5"]`) to a comma-joined list of lookup names
 * (e.g. "Gauteng, Western Cape"). Returns the raw value unchanged when it is empty, the map is
 * unavailable, or it is not a parseable id array — so the reviewer never sees a blank. Individual ids
 * with no match fall back to the id itself rather than dropping out.
 */
export const resolveLookupList = (
  rawValue: string | null,
  namesById: Record<string, string> | undefined
): string | null => {
  if (rawValue === null || rawValue === '' || !namesById) {
    return rawValue;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawValue);
  } catch {
    return rawValue;
  }
  if (!Array.isArray(parsed)) {
    return rawValue;
  }
  return parsed.map((id) => namesById[String(id)] ?? String(id)).join(', ');
};

/**
 * Substrings (case-insensitive) that mark a leaf field sensitive by default when no explicit
 * sensitivity is known for it. Mirrors the backend fallback, `DiffFieldRegistry.SensitiveKeywords`
 * (B1NG-Service-Integration-Gateway) — keep the two lists in step.
 *
 * Used only for a leaf that has NO diff entry (bug #7666's "unchanged" bucket rows): a leaf that DID
 * change already carries its authoritative `sensitive` flag straight from the backend's
 * `DiffFieldRegistry`, which seeds every currently-configured category's fields to values this same
 * keyword heuristic reproduces exactly (verified against every entry in that registry) — so this
 * fallback is not a weaker approximation for today's categories, only a documented one for whatever
 * is added next.
 */
const SENSITIVE_LEAF_KEYWORDS: readonly string[] = [
  'accountnumber',
  'accountholder',
  'swift',
  'branchcode',
  'vatnumber',
  'taxclearancenumber',
  'idnumber'
];

/** True when `leafKey` should be masked by default, absent a diff-supplied `sensitive` flag. */
export const isSensitiveLeafKeyDefault = (leafKey: string): boolean => {
  const lower = leafKey.toLowerCase();
  return SENSITIVE_LEAF_KEYWORDS.some((keyword) => lower.includes(keyword));
};

/**
 * Humanizes a camelCase/PascalCase leaf key into a label, e.g. `"accountNumber"` -> `"Account
 * number"`. Mirrors the backend fallback, `DiffFieldRegistry.Humanize` — only the first word is
 * capitalized, matching that method's documented style. Used as the display label for a bucket row
 * (bug #7666) synthesised from the snapshot rather than the diff, so it has no backend-resolved label
 * to fall back on; `resolveDiffField` still gets the final say for a schema'd category.
 */
export const humanizeLeafKey = (leafKey: string): string => {
  if (!leafKey) {
    return leafKey;
  }
  let result = '';
  for (let i = 0; i < leafKey.length; i++) {
    const c = leafKey[i];
    if (i > 0 && /[A-Z]/.test(c) && !/[A-Z]/.test(leafKey[i - 1])) {
      result += ' ';
    }
    result += c.toLowerCase();
  }
  return result.charAt(0).toUpperCase() + result.slice(1);
};

/**
 * Flattens a snapshot's JSON leaves to `path -> formatted value`, skipping ignored audit/identity
 * keys ({@link IGNORED_SNAPSHOT_KEYS}) and empty (null/blank) values — an empty leaf carries nothing
 * a reviewer needs and would otherwise show a spurious blank row (e.g. the unpopulated half of a
 * mutually-exclusive twin column pair). Object/array structure mirrors the backend
 * `DiffSummaryBuilder.Walk`/`WalkObject`/`WalkArray`: an array of records is walked by its OWN
 * positional index, which is deliberately not the same as record-content PAIRING — see
 * `buildCategoryBucketFields` for why that is safe here.
 *
 * Never throws: invalid JSON, or a snapshot that isn't an object/array at the root, yields an empty
 * map so a malformed snapshot degrades to "no extra bucket rows" rather than crashing the card.
 */
export const flattenSnapshotLeaves = (
  snapshot: string | null | undefined
): Map<string, string> => {
  const leaves = new Map<string, string>();
  if (!snapshot) {
    return leaves;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(snapshot);
  } catch {
    return leaves;
  }

  const walk = (path: string, node: unknown): void => {
    if (node === null || node === undefined) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item, index) => walk(`${path}[${index}]`, item));
      return;
    }
    if (typeof node === 'object') {
      for (const key of Object.keys(node as Record<string, unknown>)) {
        if (isIgnoredSnapshotKey(key)) {
          continue;
        }
        const childPath = path ? `${path}.${key}` : key;
        walk(childPath, (node as Record<string, unknown>)[key]);
      }
      return;
    }

    const formatted = String(node);
    if (formatted.trim() === '') {
      return;
    }
    leaves.set(path, formatted);
  };

  walk('', parsed);
  return leaves;
};

export const resolveDiffField = (
  categoryName: string | undefined,
  leafKey: string,
  backendLabel: string
): { visible: boolean; label: string } => {
  const schema = getCategorySnapshotSchema(categoryName);
  if (schema) {
    const match = schema.find(
      (field) => field.key.toLowerCase() === leafKey.toLowerCase()
    );
    return match
      ? { visible: true, label: match.label }
      : { visible: false, label: backendLabel };
  }
  return { visible: !isIgnoredSnapshotKey(leafKey), label: backendLabel };
};
